import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MeetingForm, MeetingFormData } from '@/components/meeting/MeetingForm';
import { NotesCapture } from '@/components/meeting/NotesCapture';
import { BusinessCardScanner } from '@/components/meeting/BusinessCardScanner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import toast from 'react-hot-toast';
import { Camera, FileText } from 'lucide-react';

type Step = 'choice' | 'scan' | 'form' | 'notes';
type EntryMode = 'scan' | 'manual' | null;

const NewMeetingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('choice');
  const [meetingData, setMeetingData] = useState<MeetingFormData | null>(null);
  const [meetingId, setMeetingId] = useState<number | null>(null);
  const [scannedCardData, setScannedCardData] = useState<{
    personName: string | null;
    companyName: string | null;
    position: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    address: string | null;
  } | null>(null);
  const [businessCardImage, setBusinessCardImage] = useState<string | null>(null);

  const handleChoiceSelect = (mode: EntryMode) => {
    if (mode === 'scan') {
      setStep('scan');
    } else {
      setStep('form');
    }
  };

  const handleCardScanned = (cardData: typeof scannedCardData, imageBase64: string) => {
    setScannedCardData(cardData);
    setBusinessCardImage(imageBase64);
    setStep('form');
  };

  const handleMeetingCreated = (id: number, data: MeetingFormData) => {
    setMeetingId(id);
    setMeetingData(data);
    setStep('notes');
  };

  const handleNotesComplete = () => {
    toast.success('Spotkanie i notatki zapisane!');
    navigate('/history');
  };

  const handleSkipNotes = () => {
    toast.success('Spotkanie zapisane!');
    navigate('/history');
  };

  const handleBack = () => {
    if (step === 'scan' || step === 'form') {
      setStep('choice');
      setScannedCardData(null);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Nowe spotkanie</h1>
        <div className="flex gap-2">
          {(step === 'scan' || step === 'form') && (
            <Button variant="outline" onClick={handleBack}>
              ← Wstecz
            </Button>
          )}
          {step === 'notes' && (
            <Button variant="outline" onClick={handleSkipNotes}>
              Pomiń i zakończ
            </Button>
          )}
        </div>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2">
        <div className={`flex-1 h-2 rounded-full transition-colors ${
          step === 'choice' || step === 'scan' ? 'bg-primary' : 'bg-primary/50'
        }`} />
        <div className={`flex-1 h-2 rounded-full transition-colors ${
          step === 'form' ? 'bg-primary' : step === 'notes' ? 'bg-primary/50' : 'bg-muted'
        }`} />
        <div className={`flex-1 h-2 rounded-full transition-colors ${
          step === 'notes' ? 'bg-primary' : 'bg-muted'
        }`} />
      </div>

      {/* Step 1: Choice */}
      {step === 'choice' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 xl:gap-8">
          <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => handleChoiceSelect('scan')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <Camera className="h-6 w-6 text-primary" />
                Rozpoznaj wizytówkę
              </CardTitle>
              <CardDescription>
                Zeskanuj wizytówkę i automatycznie wypełnij dane spotkania
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>✓ Zrób zdjęcie lub wybierz plik</li>
                <li>✓ AI automatycznie rozpozna dane</li>
                <li>✓ Wizytówka zostanie zapisana</li>
                <li>✓ Szybkie utworzenie spotkania</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => handleChoiceSelect('manual')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-primary" />
                Wprowadź dane ręcznie
              </CardTitle>
              <CardDescription>
                Wypełnij formularz bez wizytówki
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>✓ Pełna kontrola nad danymi</li>
                <li>✓ Podpowiedzi dla każdego pola</li>
                <li>✓ Szybkie wypełnianie formularza</li>
                <li>✓ Nie wymaga wizytówki</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 2a: Scan */}
      {step === 'scan' && (
        <BusinessCardScanner
          onCardScanned={handleCardScanned}
          onBack={handleBack}
        />
      )}

      {/* Step 2b: Form */}
      {step === 'form' && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Szczegóły spotkania</h2>
          {scannedCardData && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-medium">
                ✓ Dane z wizytówki zostały automatycznie wypełnione
              </p>
            </div>
          )}
          <MeetingForm
            onSubmit={handleMeetingCreated}
            initialData={scannedCardData}
            businessCardImage={businessCardImage}
          />
        </Card>
      )}

      {/* Step 3: Notes */}
      {step === 'notes' && meetingId && meetingData && (
        <NotesCapture
          meetingId={meetingId}
          meetingDate={meetingData.meetingDate}
          meetingTime={meetingData.meetingTime}
          location={meetingData.location}
          onFinish={handleNotesComplete}
        />
      )}
    </div>
  );
};

export default NewMeetingPage;
